(() => {
  'use strict';
  if (customElements.get('sheko-mix-match')) return;

  const languageText = (input, locale) => {
    const raw = String(input || '');
    const parts = raw.split(/\|(?=[a-z]{2}(?:-[a-z]{2})?:)/i);
    const localized = parts.map((part) => part.match(/^([a-z]{2}(?:-[a-z]{2})?):([\s\S]*)$/i)).filter(Boolean);
    if (!localized.length) return raw.trim();
    const lang = locale.toLowerCase().split('-')[0];
    return (localized.find((part) => part[1].toLowerCase().split('-')[0] === lang) || localized[0])[2].trim();
  };
  const tiersFor = (input) => (Array.isArray(input) ? input : [])
    .filter((tier) => Number.isInteger(tier.minQty) && tier.minQty > 0 && Number.isFinite(tier.percent) && tier.percent > 0 && tier.percent <= 100)
    .slice().sort((a, b) => a.minQty - b.minQty);
  const tierAt = (quantity, tiers) => tiers.reduce((best, tier) => quantity >= tier.minQty ? Math.max(best, tier.percent) : best, 0);
  // Only the builder marks lines; ordinary purchases of the same product stay separate.
  // A campaign builder (Cyber Week) uses its own marker, so the two never count each other.
  const isMixMatchLine = (item) => item.properties?._mixmatch === '1';
  const markerTest = (marker) => (marker ? (item) => item.properties?.[marker.property] === marker.value : isMixMatchLine);
  const campaignStateOf = (state, marker) => !state?.features?.builder?.active || !marker?.property ? 'inactive' : state.phase === 'club' && !state.club && !state.preview ? 'club-login' : 'active';
  const eligibleCount = (cart, ids, isLine = isMixMatchLine) => (cart?.items || []).reduce((total, item) => total + (isLine(item) && ids.has(String(item.product_id)) ? item.quantity : 0), 0);
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const image = (src, alt, className) => {
    const img = el('img', className);
    img.src = src; img.alt = alt; img.width = 600; img.height = 600; img.loading = 'lazy';
    return img;
  };
  class MixMatch extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      try { this.data = JSON.parse(this.querySelector('[data-mm-data]').textContent); } catch { return; }
      this.routines = (this.data.routines || []).map((routine) => ({ ...routine, products: routine.products.filter(Boolean) })).filter((routine) => routine.products.length);
      this.campaign = this.data.campaign && typeof this.data.campaign === 'object' ? this.data.campaign : null;
      // Campaign mode (Cyber Week): customers see only campaign-eligible products; the editor note lists the rest.
      const campaignIds = this.campaign ? new Set((this.campaign.eligibleProductIds || []).map(String)) : null;
      this.products = new Map();
      for (const [group, products] of Object.entries(this.data.groups || {})) {
        for (const product of products.filter(Boolean)) {
          if (campaignIds && !campaignIds.has(String(product.id))) continue;
          if (!this.products.has(product.id)) this.products.set(product.id, { ...product, group });
        }
      }
      for (const routine of this.routines) for (const product of routine.products) {
        const existing = this.products.get(product.id);
        if (!existing) this.products.set(product.id, product);
        else if (!existing.benefits) existing.benefits = product.benefits;
      }
      if (this.campaign) {
        // Marker and tiers default to the campaign config in the head (snippets/cyber-week-config.liquid).
        // The offer exists only while assets/cyber-week.js reports the builder feature active; anything
        // else (switch off, pending decision, outside the window, club phase without club login) shows
        // the matching notice and no products, so nothing can be added expecting a discount.
        const { state, config = {} } = window.ShekoCyberWeek || {};
        const marker = this.campaign.marker || config.marker;
        this.campaignState = campaignStateOf(state, marker);
        this.dataset.campaignState = this.campaignState;
        this.querySelectorAll('[data-mm-campaign-notice]').forEach((node) => { node.hidden = node.dataset.mmCampaignNotice !== this.campaignState; });
        // An open tab follows phase changes (checked every minute) by starting over.
        document.addEventListener('cw26:state', (event) => { if (!this.busy && campaignStateOf(event.detail, marker) !== this.campaignState) this.restart(); }, { signal: this.controller.signal });
        if (this.campaignState !== 'active') return;
        this.isLine = markerTest(marker);
        this.lineProperties = { [marker.property]: marker.value };
        this.tiers = tiersFor(this.campaign.tiers || config.tiers);
        this.eligibleIds = campaignIds;
      } else {
        this.isLine = isMixMatchLine;
        this.lineProperties = { _mixmatch: '1' };
        this.tiers = this.data.discountEnabled ? tiersFor(this.data.tiers) : [];
        this.eligibleIds = new Set(this.routines.flatMap((routine) => routine.products.map((product) => product.id)));
      }
      if (!this.products.size) return;
      this.selection = new Map();
      this.cart = null;
      this.busy = false;
      this.catalog = new Map([...this.products.values()].flatMap((product) => product.variants.map((variant) => [variant.id, { product, variant }])));
      this.money = new Intl.NumberFormat(this.data.locale, { style: 'currency', currency: this.data.currency });
      this.routineIndex = this.initialRoutine();
      this.addEventListener('click', this.onClick.bind(this), { signal: this.controller.signal });
      this.addEventListener('change', this.onChange.bind(this), { signal: this.controller.signal });
      window.addEventListener('pageshow', () => this.refreshCart(), { signal: this.controller.signal });
      window.addEventListener('focus', () => this.refreshCart(), { signal: this.controller.signal });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCart());
      }
      this.renderOffers(); this.renderProducts(); this.renderSummary();
      this.refreshCart(); this.setupLayout();
      this.querySelectorAll('dialog').forEach((dialog) => {
        dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); }, { signal: this.controller.signal });
        dialog.addEventListener('close', () => { this.dialogOpener?.focus({ preventScroll: true }); }, { signal: this.controller.signal });
      });
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null; this.unsubscribe?.(); this.observer?.disconnect(); this.resizeObserver?.disconnect();
      this.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
    }
    restart() { window.location.reload(); }
    t(key, values = {}) { return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, value), this.data.strings[key] || key); }
    format(price) { return this.money.format(price / 100); }
    find(selector) { return this.querySelector(selector); }
    initialRoutine() {
      let index = this.routines.findIndex((routine) => routine.handle === this.data.defaultRoutine);
      if (index >= 0) return index;
      if (this.data.productId) {
        index = this.routines.findIndex((routine) => routine.products.some((product) => product.id === this.data.productId && product.handle === window.ShekoMixMatchContent?.bases?.[routine.handle]));
        if (index < 0) index = this.routines.findIndex((routine) => routine.products.some((product) => product.id === this.data.productId));
      } else if (this.data.collectionId) {
        const matches = (product) => (product.collections || []).map(String).includes(this.data.collectionId);
        index = this.routines.findIndex((routine) => routine.products.some((product) => matches(product) && product.handle === window.ShekoMixMatchContent?.bases?.[routine.handle]));
        if (index < 0) index = this.routines.findIndex((routine) => routine.products.some(matches));
      }
      return Math.max(0, index);
    }
    routineFor(product) {
      return this.routines[this.routineIndex]?.products.some((item) => item.id === product.id) ? this.routines[this.routineIndex] : this.routines.find((routine) => routine.products.some((item) => item.id === product.id)) || { handle: '', products: [] };
    }
    copy(product, routine = this.routineFor(product)) {
      const content = window.ShekoMixMatchContent || {};
      const [benefit = '', ...body] = languageText(routine.products.find((item) => item.id === product.id)?.benefits || product.benefits, this.data.locale).split(/\n\s*\n/);
      const override = (this.data.overrides || []).find((entry) => entry.productId === product.id && entry.routine === routine.handle)
        || (this.data.overrides || []).find((entry) => entry.productId === product.id && !entry.routine);
      const defaults = content.copy?.[this.data.locale.split('-')[0]]?.[product.handle] || {};
      const step = override?.step || (product.group === 'shakes' || /^(abnehm-shake-|balance-shake-|shake$)/.test(product.handle) ? 'base' : product.group === 'accessories' || content.extras?.includes(product.handle) ? 'extra' : 'complement');
      return { step, benefit: override?.benefit || defaults.benefit || product.shortDescription || benefit, why: override?.why || defaults.why || body.join('\n\n'), badge: override?.badge || product.badge || '', highlight: Boolean(override?.highlight) };
    }
    renderOffers() {
      const region = this.find('[data-mm-offers]');
      if (!region) return;
      region.replaceChildren(); region.hidden = !this.tiers.length;
      let best = 0;
      for (const tier of this.tiers) {
        if (tier.percent <= best) continue;
        best = tier.percent;
        const offer = el('div', 'mm-offer');
        offer.append(el('strong', '', `${tier.percent}%`), el('span', '', this.t('offer_threshold', { count: tier.minQty })));
        region.append(offer);
      }
      const hero = this.find('[data-mm-hero-offer]');
      if (hero) { hero.hidden = !best; hero.textContent = this.t('hero_offer', { percent: best }); }
    }
    renderProducts() {
      const region = this.find('[data-mm-products]'); region.replaceChildren();
      const groups = { shakes: [], supplements: [], accessories: [] };
      for (const product of this.products.values()) {
        const step = this.copy(product).step;
        groups[step === 'base' ? 'shakes' : step === 'extra' ? 'accessories' : 'supplements'].push(product);
      }
      for (const [group, products] of Object.entries(groups)) {
        const nav = this.find(`[data-mm-step="${group}"]`);
        if (nav) nav.hidden = !products.length;
        if (!products.length) continue;
        const section = el('section', `mm-step mm-step--${group}`); section.id = `${this.id}-${group}`;
        const heading = el('h2', '', this.t(`${group}_heading`)); heading.id = `${section.id}-title`; heading.tabIndex = -1;
        section.setAttribute('aria-labelledby', heading.id); section.append(heading);
        const grid = el('div', 'mm-grid'); grid.id = `${section.id}-grid`;
        products.forEach((product, index) => { const card = this.productCard(product); card.hidden = group === 'shakes' && index >= 4 && !this.showAll; grid.append(card); });
        section.append(grid);
        if (group === 'shakes' && products.length > 4) {
          const more = el('button', 'mm-show-all', this.t(this.showAll ? 'show_less' : 'show_all', { count: products.length }));
          more.type = 'button'; more.dataset.mmShowAll = ''; more.setAttribute('aria-expanded', String(Boolean(this.showAll))); more.setAttribute('aria-controls', grid.id); section.append(more);
        }
        region.append(section);
      }
      this.syncCards();
      this.updateActiveStep();
    }
    productCard(product) {
      const copy = this.copy(product);
      const card = el('article', copy.highlight ? 'mm-product mm-product--highlight' : 'mm-product'); card.dataset.product = product.id;
      const variant = product.variants.find((entry) => entry.available) || product.variants[0];
      if (!variant) return card;
      card.dataset.variant = variant.id;
      const media = el('button', 'mm-product-media'); media.type = 'button'; media.dataset.mmDetails = product.id;
      media.setAttribute('aria-label', `${this.t('card_more')} ${product.title}`); media.setAttribute('aria-haspopup', 'dialog'); media.setAttribute('aria-controls', `MixMatchDetails-${this.dataset.sectionId}`);
      if (variant.image || product.image) media.append(image(variant.image || product.image, product.title));
      if (copy.badge) media.append(el('span', 'mm-badge', copy.badge));
      const arrow = el('span', 'mm-info-arrow', '↗'); arrow.setAttribute('aria-hidden', 'true'); media.append(arrow); card.append(media);
      const body = el('div', 'mm-product-body');
      const title = copy.step === 'base' ? product.title.replace(/^Balance Shake\s*/i, '') : product.title;
      body.append(el('h3', '', title));
      if (copy.benefit) body.append(el('p', 'mm-benefit', copy.benefit));
      const purchase = el('div', 'mm-product-purchase');
      if (product.variants.length > 1) {
        const label = el('label', 'mm-variant-label', this.t('variant'));
        const select = el('select', 'mm-variant'); select.dataset.variantSelect = ''; select.setAttribute('aria-label', `${this.t('variant')}: ${product.title}`);
        product.variants.forEach((option) => {
          const node = el('option', '', option.title + (option.available ? '' : ` — ${this.t('unavailable')}`));
          node.value = option.id; node.disabled = !option.available; node.selected = option.id === variant.id; select.append(node);
        });
        label.append(select); purchase.append(label);
      }
      const price = el('span', 'mm-product-price', this.format(variant.price)); price.dataset.price = '';
      const controls = el('div', 'mm-card-controls'); controls.dataset.mmControls = '';
      purchase.append(price, controls); body.append(purchase); card.append(body); return card;
    }
    quantityControls(id, count) {
      const { product, variant } = this.catalog.get(id);
      const controls = el('div', 'mm-quantity');
      for (const direction of [-1, 1]) {
        const control = el('button', '', direction < 0 ? '−' : '+'); control.type = 'button'; control.dataset.id = id; control.dataset.quantity = direction;
        control.disabled = this.busy || Boolean(this.pendingAdd) || (direction > 0 && variant.max != null && count + (variant.step || 1) > variant.max);
        control.setAttribute('aria-label', `${this.t(direction < 0 ? 'minus' : 'plus')}: ${product.title}, ${variant.title}`);
        controls.append(control); if (direction < 0) controls.append(el('output', '', String(count)));
      }
      return controls;
    }
    openDetails(productId, opener) {
      const product = this.products.get(productId); if (!product) return;
      const copy = this.copy(product); const body = this.find('[data-mm-detail-body]'); body.replaceChildren();
      if (product.image) body.append(image(product.image, product.title));
      const title = el('h2', '', product.title); title.id = `MixMatchDetailTitle-${this.dataset.sectionId}`; body.append(title);
      if (copy.benefit) body.append(el('p', '', copy.benefit));
      if (copy.step === 'base') body.append(el('p', '', this.t('shake_anchor')));
      if (copy.why) body.append(el('h3', '', this.t('why_combine')), el('p', '', copy.why));
      if (!this.eligibleIds.has(product.id)) body.append(el('p', 'mm-fineprint', this.t('not_eligible')));
      this.openDialog(this.find('[data-mm-dialog]'), opener);
    }
    openDialog(dialog, opener) {
      this.dialogOpener = opener; dialog.showModal(); dialog.querySelector('.mm-dialog-body').focus();
    }
    onClick(event) {
      const button = event.target.closest('button'); if (!button) return;
      if (button.hasAttribute('data-mm-close')) { button.closest('dialog').close(); return; }
      if (button.hasAttribute('data-mm-how')) this.openDialog(this.find('[data-mm-how-dialog]'), button);
      if (button.dataset.mmDetails) this.openDetails(button.dataset.mmDetails, button);
      if (button.dataset.mmStep) {
        const target = this.find(`#${this.id}-${button.dataset.mmStep}`);
        target?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
        target?.querySelector('h2').focus({ preventScroll: true });
      }
      if (button.hasAttribute('data-mm-toggle')) {
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open)); this.find('[data-mm-panel]').hidden = !open;
      }
      if (button.hasAttribute('data-mm-show-all')) {
        this.showAll = !this.showAll;
        this.querySelectorAll('.mm-step--shakes .mm-product').forEach((card, index) => { card.hidden = index >= 4 && !this.showAll; });
        button.setAttribute('aria-expanded', String(this.showAll)); button.textContent = this.t(this.showAll ? 'show_less' : 'show_all', { count: this.querySelectorAll('.mm-step--shakes .mm-product').length });
      }
      if (this.busy || this.pendingAdd) return;
      if (button.hasAttribute('data-add')) this.changeQuantity(button.closest('[data-variant]').dataset.variant, 1);
      if (button.dataset.quantity) this.changeQuantity(button.dataset.id, Number(button.dataset.quantity));
      if (button.dataset.remove) { this.selection.delete(button.dataset.remove); this.renderSummary(); this.syncCards(); }
      if (button.hasAttribute('data-mm-submit')) this.addSelection();
    }
    onChange(event) {
      if (!event.target.hasAttribute('data-variant-select')) return;
      const card = event.target.closest('[data-variant]'); const entry = this.catalog.get(event.target.value); if (!entry) return;
      card.dataset.variant = entry.variant.id;
      card.querySelector('[data-price]').textContent = this.format(entry.variant.price);
      const img = card.querySelector('img'); if (img && (entry.variant.image || entry.product.image)) img.src = entry.variant.image || entry.product.image;
      this.syncCards();
    }
    changeQuantity(id, direction) {
      const entry = this.catalog.get(id); if (!entry?.variant.available) return;
      const current = this.selection.get(id) || 0;
      if (!current && direction < 0) return;
      const min = Math.max(1, entry.variant.min || 1); const step = Math.max(1, entry.variant.step || 1);
      let quantity = current ? current + direction * step : min;
      if (quantity < min) quantity = 0;
      if (entry.variant.max != null && quantity > entry.variant.max) { this.find('[data-mm-status]').textContent = this.t('limit'); return; }
      if (quantity > 0) this.selection.set(id, quantity); else this.selection.delete(id);
      this.renderSummary(); this.syncCards();
    }
    syncCards() {
      this.querySelectorAll('.mm-product[data-variant]').forEach((card) => {
        const entry = this.catalog.get(card.dataset.variant); const quantity = this.selection.get(card.dataset.variant) || 0;
        const controls = card.querySelector('[data-mm-controls]'); const focus = controls.contains(document.activeElement) ? document.activeElement.dataset.quantity || 'add' : null;
        controls.replaceChildren();
        if (quantity) controls.append(this.quantityControls(card.dataset.variant, quantity));
        else {
          const add = el('button', 'mm-add'); add.type = 'button'; add.dataset.add = '';
          if (entry?.variant.available) { const plus = el('span', 'mm-plus', '+'); plus.setAttribute('aria-hidden', 'true'); add.append(plus, document.createTextNode(this.t('add'))); }
          else add.textContent = this.t('unavailable');
          add.disabled = !entry?.variant.available || this.busy || Boolean(this.pendingAdd); add.setAttribute('aria-label', `${this.t('add')}: ${entry.product.title}`); controls.append(add);
        }
        card.classList.toggle('is-selected', quantity > 0);
        if (focus) (controls.querySelector(`[data-quantity="${focus}"]`) || controls.querySelector('button'))?.focus({ preventScroll: true });
      });
    }
    renderSummary() {
      const region = this.find('[data-mm-selected]');
      const focused = document.activeElement;
      const restore = region.contains(focused) ? { id: focused.dataset.id || focused.dataset.remove, action: focused.dataset.quantity || 'remove' } : null;
      region.replaceChildren();
      let quantity = 0, eligible = 0, subtotal = 0;
      const thumbs = this.find('[data-mm-thumbs]'); thumbs?.replaceChildren();
      this.selection.forEach((count, id) => {
        const { product, variant } = this.catalog.get(id); quantity += count; subtotal += variant.price * count;
        if (this.eligibleIds.has(product.id)) eligible += count;
        const src = variant.image || product.image;
        if (thumbs && thumbs.childElementCount < 3) {
          const thumb = el('span', 'mm-thumb'); if (src) thumb.append(image(src, '')); thumb.append(el('small', '', String(count))); thumbs.append(thumb);
        }
        const row = el('div', 'mm-selected-row');
        if (src) row.append(image(src, '', 'mm-selected-image'));
        const body = el('div', 'mm-selected-body'); body.append(el('strong', '', product.title));
        if (variant.title !== 'Default Title') body.append(el('span', '', variant.title));
        body.append(el('span', '', this.format(variant.price * count)));
        row.append(body, this.quantityControls(id, count)); region.append(row);
      });
      if (this.selection.size > 3 && thumbs) thumbs.append(el('span', 'mm-more-count', `+${this.selection.size - 3}`));
      const existing = eligibleCount(this.cart, this.eligibleIds, this.isLine); const total = eligible + existing;
      const percent = this.cart ? tierAt(total, this.tiers) : 0;
      const next = this.tiers.find((tier) => tier.minQty > total && tier.percent > percent);
      const savings = [...this.selection].reduce((sum, [id, count]) => {
        const { product, variant } = this.catalog.get(id);
        return sum + (this.eligibleIds.has(product.id) ? Math.floor(variant.price * percent / 100) * count : 0);
      }, 0);
      const tier = this.find('[data-mm-tier]'); tier.textContent = this.cart && this.tiers.length ? next ? this.t('next', { count: next.minQty - total, percent: next.percent }) : this.t('maximum') : '';
      tier.hidden = Boolean(savings);
      if (this.campaign) {
        tier.textContent = this.cart && this.tiers.length && total ? this.campaignStatus(total, percent, next) : '';
        tier.hidden = false;
      }
      this.find('[data-mm-cart-count]').textContent = existing ? this.t('existing', { count: existing }) : '';
      this.find('[data-mm-savings]').hidden = !savings || Boolean(this.campaign);
      this.find('[data-mm-savings-value]').textContent = this.format(savings);
      this.find('[data-mm-savings-context]').textContent = this.t('savings_context', { percent });
      this.find('[data-mm-total-label]').textContent = this.t(quantity === 1 ? 'total_label_one' : 'total_label', { count: quantity });
      this.find('[data-mm-total]').textContent = this.format(subtotal - savings);
      const submit = this.find('[data-mm-submit]'); submit.disabled = !quantity || this.busy || Boolean(this.pendingAdd);
      if (!quantity && !this.busy && !this.pendingAdd) { this.find('[data-mm-panel]').hidden = true; this.find('[data-mm-toggle]').setAttribute('aria-expanded', 'false'); }
      this.find('[data-mm-bar]').hidden = (!quantity && !this.busy && !this.pendingAdd && !this.find('[data-mm-status]').textContent) || this.sectionVisible === false;
      if (restore) {
        const target = [...region.querySelectorAll('button')].find((node) => node.dataset.id === restore.id && node.dataset.quantity === restore.action);
        (target || this.find('[data-mm-toggle]')).focus({ preventScroll: true });
      }
    }
    // "Du hast 3 Stück, 15 % Rabatt, Du sparst 13,48 €. Noch 1 Stück bis 20 %." The saving covers every
    // campaign unit (cart and selection) at the reached tier, rounded per line like a line discount.
    campaignStatus(total, percent, next) {
      const strings = this.campaign.strings || {};
      const fill = (text, values) => Object.entries(values).reduce((out, [name, value]) => out.replaceAll(`{${name}}`, value), text || '');
      const inCart = (this.cart?.items || []).filter((item) => this.isLine(item) && this.eligibleIds.has(String(item.product_id)))
        .reduce((sum, item) => sum + Math.round((item.original_line_price ?? item.price * item.quantity) * percent / 100), 0);
      const selected = [...this.selection].reduce((sum, [id, count]) => {
        const { product, variant } = this.catalog.get(id);
        return sum + (this.eligibleIds.has(product.id) ? Math.round(variant.price * count * percent / 100) : 0);
      }, 0);
      let text = fill(strings.status_count, { count: total });
      if (percent) text += fill(strings.status_discount, { percent, saving: this.format(inCart + selected) });
      text += '.';
      const tail = next ? fill(strings.status_next, { missing: next.minQty - total, percent: next.percent }) : fill(strings.status_max, { percent });
      return tail ? `${text} ${tail}` : text;
    }
    async refreshCart() {
      const requestId = this.cartRequest = (this.cartRequest || 0) + 1;
      try {
        const response = await fetch(`${this.data.cartUrl}.js`, { headers: { Accept: 'application/json' }, signal: this.controller.signal });
        if (!response.ok) throw new Error('Cart read failed');
        const cart = await response.json();
        if (requestId !== this.cartRequest || !this.isConnected) return;
        this.cart = cart;
        if (this.pendingAdd?.reconcile) {
          for (const [id, count] of this.pendingAdd.items) {
            const before = this.pendingAdd.before.items.filter((line) => this.isLine(line) && String(line.variant_id) === id).reduce((sum, line) => sum + line.quantity, 0);
            const after = cart.items.filter((line) => this.isLine(line) && String(line.variant_id) === id).reduce((sum, line) => sum + line.quantity, 0);
            const remaining = Math.max(0, count - Math.max(0, after - before));
            if (remaining) this.selection.set(id, remaining); else this.selection.delete(id);
          }
          this.pendingAdd = null; this.syncCards();
        }
        this.renderSummary();
        return true;
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.cartRequest) return;
        this.cart = null; this.find('[data-mm-status]').textContent = this.t('cart_error'); this.renderSummary();
      }
    }
    async addSelection() {
      if (this.busy || this.pendingAdd || !this.selection.size) return;
      this.busy = true; this.renderSummary(); this.syncCards();
      this.querySelectorAll('select').forEach((node) => { node.disabled = true; });
      const button = this.find('[data-mm-submit]'); this.find('[data-mm-submit-label]').textContent = this.t('adding');
      const status = this.find('[data-mm-status]'); status.textContent = '';
      const drawer = document.querySelector('cart-drawer');
      const items = [...this.selection].map(([id, quantity]) => ({ id, quantity, properties: { ...this.lineProperties } }));
      let accepted = false;
      try {
        if (!await this.refreshCart()) throw new Error(this.t('cart_error'));
        this.pendingAdd = { before: this.cart, items: [...this.selection], reconcile: false };
        const sections = drawer?.getSectionsToRender?.().map((section) => section.id) || ['cart-icon-bubble'];
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sections.slice(0, 5), sections_url: window.location.pathname }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.t('error'));
        accepted = true; this.pendingAdd = null; this.selection.clear();
        status.textContent = this.t('success');
        await this.refreshCart();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mix-match', cartData: result })).catch(() => {});
        }
        if (drawer && result.sections?.['cart-drawer']) {
          drawer.setActiveElement?.(button); drawer.classList.remove('is-empty'); drawer.renderContents(result);
        } else {
          window.location.assign(this.data.cartUrl);
        }
      } catch (error) {
        // A failed section render after a successful add must never permit adding the same selection again.
        status.textContent = accepted ? this.t('success') : `${this.t('error')} ${error.message === this.t('error') ? '' : error.message}`;
        if (!accepted) {
          if (this.pendingAdd) this.pendingAdd.reconcile = true;
          await this.refreshCart();
        }
      } finally {
        this.busy = false; this.find('[data-mm-submit-label]').textContent = this.t('add_all');
        this.querySelectorAll('select').forEach((node) => { node.disabled = false; });
        this.renderSummary(); this.syncCards();
      }
    }
    updateActiveStep() {
      const header = document.querySelector('.section-header');
      const pinned = header && ['sticky', 'fixed'].includes(getComputedStyle(header).position);
      this.style.setProperty('--mm-header-offset', `${pinned ? Math.max(0, header.getBoundingClientRect().bottom) : 0}px`);
      const sections = [...this.querySelectorAll('.mm-step')];
      const nav = this.find('.mm-nav'); if (!nav) return;
      const boundary = nav.getBoundingClientRect().bottom + 24;
      const current = sections.filter((section) => section.getBoundingClientRect().top <= boundary).at(-1) || sections[0];
      this.querySelectorAll('[data-mm-step]').forEach((button) => {
        if (button.getAttribute('aria-controls') === current?.id) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
      });
    }
    setupLayout() {
      window.addEventListener('scroll', () => this.updateActiveStep(), { passive: true, signal: this.controller.signal });
      if (typeof IntersectionObserver !== 'undefined') {
        this.observer = new IntersectionObserver(([entry]) => { this.sectionVisible = entry.isIntersecting; this.renderSummary(); }); this.observer.observe(this);
      }
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateActiveStep();
          const bar = this.find('[data-mm-bar]'); this.style.setProperty('--mm-bar-height', `${bar.offsetHeight}px`);
        }); this.resizeObserver.observe(this.find('[data-mm-bar]'));
        const header = document.querySelector('.section-header'); if (header) this.resizeObserver.observe(header);
      }
    }
  }
  customElements.define('sheko-mix-match', MixMatch);
})();
